import { z } from 'zod';

import type { MarketplacePropertySuggestionFragment } from './__generated__/location-search.zeus';
import type { MapLocation } from './types';

export const RECENT_LOCATION_SEARCHES_KEY = 'opendoor.cosmos.marketplace.recent-location-searches';
export const MAX_RECENT_LOCATION_SEARCHES = 5;

const marketplaceZoneTypeSchema = z.enum([
  'CITY',
  'COUNTY',
  'MACRO_NEIGHBORHOOD',
  'NEIGHBORHOOD',
  'RESIDENTIAL_BOUNDARY',
  'STATE',
  'SUBNEIGHBORHOOD',
  'UNKNOWN',
  'ZIP_CODE',
]);

const recentZoneEntrySchema = z.object({
  kind: z.literal('zone'),
  value: z.string().min(1),
  label: z.string().min(1),
  zoneType: marketplaceZoneTypeSchema,
  coordinates: z.tuple([z.number(), z.number()]).optional(),
});

const recentListingEntrySchema = z.object({
  kind: z.literal('listing'),
  addressId: z.string().min(1),
  fullAddress: z.string().nullable(),
  listPriceCents: z.number().nullable(),
});

const recentLocationSearchEntrySchema = z.discriminatedUnion('kind', [
  recentZoneEntrySchema,
  recentListingEntrySchema,
]);

const recentLocationSearchesSchema = z.array(recentLocationSearchEntrySchema);

export type RecentLocationSearchEntry = z.infer<typeof recentLocationSearchEntrySchema>;

export const recentEntryIdentity = (entry: RecentLocationSearchEntry): string =>
  entry.kind === 'zone' ? `zone:${entry.value}` : `listing:${entry.addressId}`;

export const recentEntryDisplayName = (entry: RecentLocationSearchEntry): string => {
  if (entry.kind === 'zone') {
    return entry.label;
  }
  return entry.fullAddress ?? 'Listing';
};

export const recentEntryFromLocation = (location: MapLocation): RecentLocationSearchEntry => ({
  kind: 'zone',
  value: location.value,
  label: location.label,
  zoneType: location.zoneType,
  ...(location.coordinates ? { coordinates: location.coordinates } : {}),
});

export const recentEntryFromAddress = (
  address: MarketplacePropertySuggestionFragment,
): RecentLocationSearchEntry => ({
  kind: 'listing',
  addressId: address.addressId,
  fullAddress: address.fullAddress,
  listPriceCents: address.listPriceCents,
});

export const mapLocationFromRecentEntry = (
  entry: Extract<RecentLocationSearchEntry, { kind: 'zone' }>,
): MapLocation => ({
  label: entry.label,
  value: entry.value,
  zoneType: entry.zoneType,
  ...(entry.coordinates ? { coordinates: entry.coordinates } : {}),
});

export const addressFromRecentEntry = (
  entry: Extract<RecentLocationSearchEntry, { kind: 'listing' }>,
): MarketplacePropertySuggestionFragment => ({
  addressId: entry.addressId,
  fullAddress: entry.fullAddress,
  listPriceCents: entry.listPriceCents,
});

export const commitRecentLocationSearch = (
  entries: RecentLocationSearchEntry[],
  next: RecentLocationSearchEntry,
): RecentLocationSearchEntry[] => {
  const identity = recentEntryIdentity(next);
  return [next, ...entries.filter((entry) => recentEntryIdentity(entry) !== identity)].slice(
    0,
    MAX_RECENT_LOCATION_SEARCHES,
  );
};

export const removeRecentLocationSearch = (
  entries: RecentLocationSearchEntry[],
  identity: string,
): RecentLocationSearchEntry[] =>
  entries.filter((entry) => recentEntryIdentity(entry) !== identity);

export const readRecentLocationSearches = (): RecentLocationSearchEntry[] => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(RECENT_LOCATION_SEARCHES_KEY);
    if (!raw) {
      return [];
    }
    let value: unknown;
    try {
      value = JSON.parse(raw);
    } catch {
      window.localStorage.removeItem(RECENT_LOCATION_SEARCHES_KEY);
      return [];
    }
    const parsed = recentLocationSearchesSchema.safeParse(value);
    if (!parsed.success) {
      window.localStorage.removeItem(RECENT_LOCATION_SEARCHES_KEY);
      return [];
    }
    return parsed.data.slice(0, MAX_RECENT_LOCATION_SEARCHES);
  } catch {
    return [];
  }
};

export const writeRecentLocationSearches = (entries: RecentLocationSearchEntry[]): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(RECENT_LOCATION_SEARCHES_KEY, JSON.stringify(entries));
  } catch {
    // Private mode / quota: recent history is best-effort.
  }
};
